"use client";

import Link from "next/link";
import {
  Wrench,
  Banknote,
  CalendarDays,
  BellOff,
  MapPin,
  ClipboardCheck,
  Users,
  UserPlus,
  Inbox,
  CheckCircle2,
} from "lucide-react";
import Logo from "@/components/Logo";

const VOORDELEN = [
  {
    icon: Banknote,
    title: "Inkomsten op basis van prestatie",
    desc: "Je ontvangt een marktconform tarief per afgeronde job. Meer klussen, meer verdienen.",
  },
  {
    icon: CalendarDays,
    title: "Jij bepaalt wanneer je werkt",
    desc: "Stel je eigen beschikbaarheid in — per dag, dagdeel of week. Volledig flexibel.",
  },
  {
    icon: BellOff,
    title: "Geen koude acquisitie",
    desc: "Jobs komen naar jou. Geen telefoontjes plegen, geen offertes schrijven.",
  },
  {
    icon: MapPin,
    title: "Jobs in jouw regio",
    desc: "We matchen op basis van jouw postcode en werkradius. Nooit ver van huis.",
  },
  {
    icon: ClipboardCheck,
    title: "Administratieve ontzorging",
    desc: "Planning, bevestigingen en joboverzicht — wij regelen het digitaal voor je.",
  },
  {
    icon: Users,
    title: "Netwerk van professionals",
    desc: "Maak deel uit van een groeiend netwerk van 101+ gecertificeerde witgoedmonteurs.",
  },
];

const STAPPEN = [
  {
    num: "01",
    icon: UserPlus,
    title: "Maak een profiel aan",
    desc: "Vul je expertise, werkgebied en beschikbaarheid in. Klaar in een paar minuten via onze wizard.",
  },
  {
    num: "02",
    icon: Inbox,
    title: "Ontvang passende jobs",
    desc: "We matchen automatisch op jouw vaardigheden, merkcertificering, regio en planning.",
  },
  {
    num: "03",
    icon: CheckCircle2,
    title: "Klus en verdien",
    desc: "Accepteer een job, voer hem uit en rond hem af via de app. Transparant en eenvoudig.",
  },
];

const VEREISTEN = [
  "KvK-inschrijving als ZZP of eenmanszaak",
  "Aantoonbare ervaring met witgoedapparaten",
  "Eigen gereedschap en vervoer",
  "Beschikbaarheid voor minimaal 5 jobs per week",
];

export default function MonteurWorden() {
  return (
    <div className="flex min-h-screen flex-col bg-[var(--surface)]">

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 border-b border-[var(--line)] bg-[color-mix(in_srgb,var(--surface)_88%,transparent)] backdrop-blur-md w-full">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3.5">
          <Link href="/"><Logo size={18} /></Link>
          <div className="flex items-center gap-6">
            <Link href="/login" className="hidden text-sm text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors sm:block">
              Inloggen
            </Link>
            <Link
              href="/signup?role=monteur"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-4 py-2 text-sm font-medium text-[#0B0E0C] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <Wrench size={13} strokeWidth={1.75} />
              Aanmelden
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-6 pb-0 pt-20 sm:pt-28">
        <p className="mb-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
          <span className="inline-block h-px w-5 bg-[var(--line)]" />
          Voor witgoedmonteurs
        </p>
        <h1 className="max-w-[620px] text-[clamp(34px,5.5vw,54px)] font-semibold leading-[1.06] tracking-[-0.028em] text-[var(--ink)]">
          Meer klussen,<br />
          <span className="text-[var(--brand-strong)]">minder zoeken.</span>
        </h1>
        <p className="mt-6 max-w-[500px] text-[16px] leading-[1.7] text-[var(--ink-2)]">
          Meld je aan als witgoedmonteur via MatchRite en ontvang jobs die passen bij jouw expertise, regio en planning. Geen acquisitie, geen gedoe — gewoon werken.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link
            href="/signup?role=monteur"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3 text-[15px] font-medium text-[#0B0E0C] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            <Wrench size={15} strokeWidth={1.75} color="#0B0E0C" />
            Start je aanmelding
          </Link>
          <a
            href="#hoe-het-werkt"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-6 py-3 text-[15px] font-medium text-[var(--ink-2)] transition-all hover:-translate-y-0.5 hover:border-[var(--ink)]"
          >
            Meer ontdekken
          </a>
        </div>
      </section>

      {/* ── Stats ───────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-6 pt-20 pb-0">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { value: "101+",     label: "Actieve monteurs" },
            { value: "10.001+",  label: "Reparaties afgerond" },
            { value: "Flexibel", label: "Jouw eigen planning" },
            { value: "NL",       label: "Landelijke dekking" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-[var(--surface-raised)] px-6 py-5 shadow-sm">
              <p className="text-[clamp(18px,2.5vw,24px)] font-extrabold tracking-[-0.03em] text-[var(--ink)]">{s.value}</p>
              <p className="mt-1 text-[12px] text-[var(--ink-3)]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Voordelen ───────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-6 py-24">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
          Wat wij je bieden
        </p>
        <h2 className="mb-12 text-[clamp(24px,3.5vw,32px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--ink)]">
          Dé boost voor jouw onderneming
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {VOORDELEN.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex flex-col gap-3 rounded-2xl bg-[var(--surface-raised)] p-6 shadow-sm">
              <div className="inline-flex w-fit items-center justify-center rounded-lg bg-[var(--brand-soft)] p-2.5">
                <Icon size={16} strokeWidth={1.75} className="text-[var(--brand-strong)]" />
              </div>
              <p className="text-[14px] font-semibold leading-snug tracking-[-0.01em] text-[var(--ink)]">{title}</p>
              <p className="text-[13px] leading-[1.6] text-[var(--ink-2)]">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Hoe het werkt ───────────────────────────────────────────── */}
      <section id="hoe-het-werkt" className="bg-[var(--surface-raised)] py-24">
        <div className="mx-auto max-w-5xl px-6">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
            Hoe het werkt
          </p>
          <h2 className="mb-14 text-[clamp(24px,3.5vw,32px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--ink)]">
            Van aanmelding tot eerste klus
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {STAPPEN.map(({ num, icon: Icon, title, desc }) => (
              <div key={num} className="relative overflow-hidden rounded-3xl bg-[var(--surface)] p-8 shadow-sm">
                <span className="absolute right-4 top-2 select-none font-mono text-[72px] font-extrabold leading-none text-[var(--brand)] opacity-[0.07]">
                  {num}
                </span>
                <div className="mb-5 inline-flex items-center justify-center rounded-xl bg-[var(--brand-soft)] p-3">
                  <Icon size={20} strokeWidth={1.75} className="text-[var(--brand-strong)]" />
                </div>
                <p className="mb-2 text-[16px] font-semibold leading-snug tracking-[-0.015em] text-[var(--ink)]">{title}</p>
                <p className="text-[13px] leading-[1.6] text-[var(--ink-2)]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Vereisten ───────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-6 py-24">
        <div className="grid gap-12 sm:grid-cols-2 sm:items-center">
          <div>
            <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "#E3852A" }}>
              Klaar om te starten?
            </p>
            <h2 className="mb-4 text-[clamp(24px,3.5vw,32px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--ink)]">
              Dit heb je nodig
            </h2>
            <p className="mb-8 text-[14px] leading-[1.7] text-[var(--ink-2)]">
              Aanmelden kost je 5 minuten. Na verificatie door ons team kun je direct aan de slag.
            </p>
            <Link
              href="/signup?role=monteur"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3 text-[15px] font-medium text-[#0B0E0C] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <Wrench size={15} strokeWidth={1.75} color="#0B0E0C" />
              Aanmelden als monteur
            </Link>
          </div>
          <ul className="space-y-4">
            {VEREISTEN.map((v) => (
              <li key={v} className="flex items-start gap-3 rounded-2xl bg-[var(--surface-raised)] px-5 py-4 shadow-sm">
                <CheckCircle2 size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-[var(--brand)]" />
                <p className="text-[14px] leading-[1.6] text-[var(--ink-2)]">{v}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-6 pb-24">
        <div className="rounded-3xl bg-[var(--brand)] p-10 sm:p-14">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "#c4621a" }}>
            Word onderdeel van MatchRite
          </p>
          <h2 className="mb-4 text-[clamp(24px,3.5vw,34px)] font-semibold leading-tight tracking-[-0.025em] text-[#0B0E0C]">
            Klaar voor je eerste job?
          </h2>
          <p className="mb-8 max-w-[420px] text-[14px] leading-[1.7] text-[#0B0E0C]/65">
            Maak gratis een profiel aan en ontvang jobs die passen bij wie jij bent als monteur.
          </p>
          <Link
            href="/signup?role=monteur"
            className="inline-flex items-center gap-2 rounded-full bg-[#0B0E0C] px-6 py-3 text-[15px] font-medium text-[var(--brand)] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <Wrench size={15} strokeWidth={1.75} />
            Start gratis aanmelding
          </Link>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-t border-[var(--line)] bg-[var(--surface-raised)] px-6 py-8">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <Link href="/"><Logo size={16} /></Link>
          <p className="font-mono text-[11px] text-[var(--ink-3)]">© 2026 MatchRite · KvK 63735458</p>
        </div>
      </footer>

    </div>
  );
}
